import React, { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { companyInfo } from "../data/mock";
import {
  Mail,
  Send,
  Calendar,
  ClipboardList,
  CheckCircle2,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    inquiryType: "igaming",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.message.trim()) errors.message = "Message is required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        inquiryType: formData.inquiryType,
        message: formData.message,
      });

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success(response.data.message || "Your message has been sent! We'll be in touch soon.");

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          inquiryType: "audit",
          message: "",
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      setIsSubmitting(false);
      const message = error.response?.data?.detail || "Failed to send message. Please try again.";
      setErrorMessage(message);
      toast.error(message);
      console.error("Contact form error:", error);
    }
  };

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0 graph-bg" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-secondary border border-border text-primary text-sm font-mono uppercase tracking-wider mb-6">
              Get Started
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Talk to an Architect
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Tell us about your SCADA, ADAS/AV, or iGaming data challenge, or send a general
              inquiry. We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-foreground mb-8">Contact Information</h2>

              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm font-medium mb-1">Email</p>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-foreground font-semibold hover:text-primary transition-colors"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Options */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground mb-4">Quick Actions</h3>
                <Card className="bg-secondary/60 border-border hover:border-primary/40 hover:shadow-md transition-all cursor-pointer group">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center group-hover:bg-primary/10 transition-colors flex-shrink-0">
                      <ClipboardList className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-foreground font-medium text-sm">Request Architecture Review</h3>
                      <p className="text-muted-foreground text-xs">Free infrastructure assessment</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto group-hover:text-primary transition-colors flex-shrink-0" />
                  </CardContent>
                </Card>

                <Card className="bg-secondary/60 border-border hover:border-primary/40 hover:shadow-md transition-all cursor-pointer group">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center group-hover:bg-primary/10 transition-colors flex-shrink-0">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-foreground font-medium text-sm">Schedule Consultation</h3>
                      <p className="text-muted-foreground text-xs">30-min discovery call</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto group-hover:text-primary transition-colors flex-shrink-0" />
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="bg-card border-border shadow-lg">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-foreground text-2xl">Send Us a Message</CardTitle>
                  <p className="text-muted-foreground text-sm mt-2">We read every message and respond promptly</p>
                </CardHeader>
                <CardContent className="pt-8">
                  {isSubmitted ? (
                    <div className="py-16 text-center">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold text-foreground mb-3">Message Sent Successfully!</h3>
                      <p className="text-muted-foreground mb-2">Thank you for reaching out to Grafodata.</p>
                      <p className="text-muted-foreground text-sm">We'll be in touch within 24 hours. Keep an eye on your inbox!</p>
                    </div>
                  ) : (
                    <>
                      {errorMessage && (
                        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-foreground font-medium">Error</p>
                            <p className="text-destructive text-sm">{errorMessage}</p>
                          </div>
                        </div>
                      )}
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-foreground font-semibold">
                              Full Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="John Smith"
                              className={`bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                                validationErrors.name ? "border-destructive" : ""
                              }`}
                            />
                            {validationErrors.name && (
                              <p className="text-destructive text-sm mt-1">{validationErrors.name}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-foreground font-semibold">
                              Work Email <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="john@company.com"
                              className={`bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                                validationErrors.email ? "border-destructive" : ""
                              }`}
                            />
                            {validationErrors.email && (
                              <p className="text-destructive text-sm mt-1">{validationErrors.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="company" className="text-foreground font-semibold">
                              Company
                            </Label>
                            <Input
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="Acme Inc."
                              className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-foreground font-semibold">
                              Phone Number
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+1 (555) 000-0000"
                              className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Label className="text-foreground font-semibold">What's This About?</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[
                              { value: "igaming", label: "iGaming" },
                              { value: "battery-tech", label: "Battery Tech" },
                              { value: "automotive", label: "Automotive/ADAS" },
                              { value: "other", label: "Other" },
                            ].map((type) => (
                              <button
                                key={type.value}
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({ ...prev, inquiryType: type.value }))
                                }
                                className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                  formData.inquiryType === type.value
                                    ? "bg-primary text-primary-foreground shadow-md"
                                    : "bg-secondary border border-border text-secondary-foreground hover:border-primary/40"
                                }`}
                              >
                                {type.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-foreground font-semibold">
                            Message <span className="text-destructive">*</span>
                          </Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your data pipeline, infrastructure, and what's not working today..."
                            rows={5}
                            className={`bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none ${
                              validationErrors.message ? "border-destructive" : ""
                            }`}
                          />
                          {validationErrors.message && (
                            <p className="text-destructive text-sm mt-1">{validationErrors.message}</p>
                          )}
                          <p className="text-muted-foreground text-xs">
                            {formData.message.length}/1000
                          </p>
                        </div>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-lg shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg
                                className="animate-spin h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Sending...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              Send Message
                              <Send className="w-5 h-5" />
                            </span>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
